import { META_IMAGES, OG_IMAGES } from '@/v2/constants/images/meta'
import { PUBLIC_MAIN_ROUTES } from '@/v2/constants/routes'
import { OGImageType } from '@/v2/types/meta'

export const ogImages: OGImageType[] = [
  {
    url: OG_IMAGES.BASE_URL.concat('/residential-storage-fee.jpg'),
    alt: 'residential storage fee page og',
    width: 1200,
    height: 630
  }
]

export const metaImages: OGImageType[] = [
  {
    url: META_IMAGES.DEFAULT,
    alt: 'residential storage fee page search image',
    width: 92,
    height: 92
  },
  ...ogImages
]

export const heroSectionContent = {
  title: 'Affordable<span> & Transparent</span> <span>Residential Storage Fees</span>',
  description:
    'At ShipAndStorage, we offer clear, customizable residential storage fees to fit your needs. Whether downsizing, decluttering, or moving temporarily, we provide cost-effective, secure storage solutions.',
  image: {
    url: '/images/v2/pages/main-pages/residential-storage-fee/hero-image.jpg',
    alt: 'Affordable & Transparent Residential Storage Fees'
  },
  ctaButton: { href: PUBLIC_MAIN_ROUTES.BOOK_STORAGE_FACILITY.href, text: 'Explore Affordable Plans' }
}

export const whyWeSectionContent = {
  title: 'What Influences Your Residential Storage Fee?',
  description: 'Several key factors affect the cost of your residential storage:',
  ctaButton: { href: PUBLIC_MAIN_ROUTES.BOOK_STORAGE_FACILITY.href, text: 'Get a Quote' },

  cards: [
    {
      id: 1,
      title: 'Storage Unit Size',
      description: 'Larger units for furniture or bulky items cost more than smaller ones for boxes or seasonal goods.',
      image: {
        src: '/images/v2/pages/main-pages/residential-storage-fee/storage-unit-size.svg',
        alt: 'Storage Unit Size'
      }
    },
    {
      id: 2,
      title: 'Storage Period',
      description: 'Long-term commitments often come with discounted rates compared to short-term storage.',
      image: {
        src: '/images/v2/pages/main-pages/residential-storage-fee/storage-period.svg',
        alt: 'Storage Period'
      }
    },
    {
      id: 3,
      title: 'Added Services',
      description: 'Options like climate control, pickup, and delivery add convenience but may increase the fee.',
      image: {
        src: '/images/v2/pages/main-pages/residential-storage-fee/added-services.svg',
        alt: 'Added Services'
      }
    },
    {
      id: 4,
      title: 'Location of Facility',
      description: 'Urban or high-demand areas may have slightly higher rates, balancing convenience with cost.',
      image: {
        src: '/images/v2/pages/main-pages/residential-storage-fee/location-of-facility.svg',
        alt: 'Location of Facility'
      }
    }
  ]
}

export const serviceTipsSectionContent = {
  title: 'Transparent and Affordable Storage Fees Explained',
  description: 'Our residential storage fees come with customizable options, so you can choose what works for you.',

  cards: [
    {
      id: 1,
      title: 'No Hidden Charges',
      description: "Transparent pricing ensures you know exactly what you're paying for with no surprise fees.",
      image: {
        src: '/images/v2/pages/main-pages/residential-storage-fee/no-hidden-charges.svg',
        alt: 'No Hidden Charges'
      }
    },
    {
      id: 2,
      title: 'Customizable Add-Ons',
      description: 'Add services like insurance, climate control, or packing assistance to match your storage needs.',
      image: {
        src: '/images/v2/pages/main-pages/residential-storage-fee/customizable-add-ons.svg',
        alt: 'Customizable Add-Ons'
      }
    },
    {
      id: 3,
      title: 'Budget-Friendly Solutions',
      description: 'Whether short-term or long-term, our flexible plans are designed to fit your budget.',
      image: {
        src: '/images/v2/pages/main-pages/residential-storage-fee/budget-friendly-solutions.svg',
        alt: 'Budget-Friendly Solutions'
      }
    },
    {
      id: 4,
      title: 'Flexible Storage Sizes',
      description: 'Choose from a variety of unit sizes to only pay for the space you truly need.',
      image: {
        src: '/images/v2/pages/main-pages/residential-storage-fee/flexible-storage-sizes.svg',
        alt: 'Flexible Storage Sizes'
      }
    },
    {
      id: 5,
      title: 'Easy Payment Options',
      description: 'Benefit from convenient and secure payment methods, ensuring a smooth billing process.',
      image: {
        src: '/images/v2/pages/main-pages/residential-storage-fee/easy-payment-options.svg',
        alt: 'Easy Payment Options'
      }
    },
    {
      id: 6,
      title: 'Scalable Pricing',
      description: 'Adjust your plan as your needs evolve, ensuring you never pay more than necessary.',
      image: {
        src: '/images/v2/pages/main-pages/residential-storage-fee/scalable-pricing.svg',
        alt: 'Scalable Pricing'
      }
    }
  ]
}

export const helperSectionContent = {
  title: 'Services Included in Our Residential Storage Fee',

  articles: [
    {
      content: [
        {
          id: 1,
          title: 'Secure Storage Units',
          description: 'Enjoy peace of mind with 24/7 surveillance, controlled access, and fire protection systems.'
        },
        {
          id: 2,
          title: 'Climate-Controlled Storage',
          description: 'Safeguard temperature-sensitive items like furniture, artwork, or electronics.'
        },
        {
          id: 3,
          title: 'Pickup and Delivery Services',
          description: 'Streamline your storage process with hassle-free transportation options.'
        },
        {
          id: 4,
          title: 'Insurance Coverage',
          description: 'Add an extra layer of protection for valuable items with affordable insurance plans.'
        }
      ],
      image: {
        src: '/images/v2/pages/main-pages/residential-storage-fee/services-included-in-our-residential-storage-fee.png',
        alt: 'Services Included in Our Residential Storage Fee'
      }
    }
  ]
}

export const whyOurResidentialSectionContent = {
  title: 'Why Our Residential Storage Fee Is Worth It',

  cards: [
    {
      id: 1,
      title: 'Transparent Pricing',
      description: 'Every cost is detailed upfront, helping you plan your budget with confidence.',
      image: {
        src: '/images/v2/pages/main-pages/residential-storage-fee/transparent-pricing.svg',
        alt: 'Transparent Pricing'
      }
    },
    {
      id: 2,
      title: 'Secure Facilities',
      description: 'Our storage units feature 24/7 monitoring, restricted access, and fire safety systems.',
      image: {
        src: '/images/v2/pages/main-pages/residential-storage-fee/secure-facilities.svg',
        alt: 'Secure Facilities'
      }
    },
    {
      id: 3,
      title: 'Climate Control Options',
      description: 'Protect sensitive items from temperature and humidity fluctuations.',
      image: {
        src: '/images/v2/pages/main-pages/residential-storage-fee/climate-control-options.svg',
        alt: 'Climate Control Options'
      }
    },
    {
      id: 4,
      title: 'Flexible Plans',
      description: 'Adjust your storage package to suit your evolving needs with ease.',
      image: {
        src: '/images/v2/pages/main-pages/residential-storage-fee/flexible-plans.svg',
        alt: 'Flexible Plans'
      }
    }
  ]
}

export const promotionalBannerSectionContent = {
  id: 1,
  title: 'Enjoy Transparent & Affordable Storage Rates',
  description:
    'Explore our affordable and transparent residential storage fees. No hidden costs, just flexible pricing for all your storage requirements.',
  image: {
    src: '/images/v2/pages/main-pages/residential-storage-fee/promotional-banner-image.png',
    alt: 'Enjoy Transparent & Affordable Storage Rates'
  },
  ctaButton: { href: PUBLIC_MAIN_ROUTES.MOVING_BOX_STORAGE.href, text: 'Choose Your Plan Today' }
}

export const faqSectionContent = {
  title: 'Frequently Asked Questions',

  questions: [
    {
      id: 1,
      question: 'What is included in the residential storage fee?',
      answer:
        'Your fee includes secure storage, optional climate control, and flexible access. Additional services like pickup, delivery, and insurance are available at an extra cost.'
    },
    {
      id: 2,
      question: 'Can I adjust my storage plan if my needs change?',
      answer: 'Yes, you can modify your plan to increase space, extend the duration, or add services as needed.'
    },
    {
      id: 3,
      question: 'How can I estimate my residential storage fee?',
      answer: 'Use our online calculator or contact us for a personalized quote based on your specific needs.'
    },
    {
      id: 4,
      question: 'Are there discounts for long-term storage?',
      answer: 'Absolutely! We offer discounted rates for customers who commit to long-term storage solutions.'
    },
    {
      id: 5,
      question: 'Are there hidden fees in the pricing?',
      answer: 'No, our pricing is completely transparent, and you’ll only pay for the services you select.'
    }
  ]
}
